import BaseComponent from "@js/components/base_component.jsx";
import { INK, formatCount } from "./theme";

// One of the configuration cards along the top of the home screen: a heading
// that links to the thing it counts, the count itself, and a short breakdown
// beneath it.
//
// Props:
//   title  — the heading, which links to `href`
//   href   — where the heading goes
//   value  — the headline count
//   accent — the color of the card's top rule
//   glyph  — a Font Awesome class ("fa-inbox") picturing what the card counts
//   rows   — [{ label, value, href, color }]; `color` draws the state dot
//
// The cards are laid out on a grid that gives every cell the same width and
// height, so the card fills its cell and pushes its rows to the bottom — a
// card with fewer rows still lines its heading and count up with its
// neighbours.
class HomeStatCard extends BaseComponent {

  render() {
    const { title, href, value, accent, glyph, rows } = this.props;

    return (
      <div
        className="ibox"
        style={{
          position: "relative",
          height: "100%",
          margin: 0,
          display: "flex",
          flexDirection: "column",
          background: INK.surface,
          border: `1px solid ${INK.border}`,
          borderTop: `3px solid ${accent}`,
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        {glyph && this._renderGlyph(glyph, accent)}

        <div style={{ padding: "14px 16px 4px" }}>
          {/* Only the title is kept clear of the glyph's corner: it shares that
              line, while the count below it starts under the glyph's baseline
              and has the full width to itself. */}
          <div style={{ paddingRight: glyph ? 28 : 0 }}>
            <a href={href} style={{ color: INK.title, fontSize: 13 }}>{title}</a>
          </div>

          <div style={{ fontSize: 30, fontWeight: 600, color: INK.primary, lineHeight: 1.2 }}>
            {formatCount(value)}
          </div>
        </div>

        <div style={{ marginTop: "auto", padding: "8px 16px 14px" }}>
          {(rows || []).map(row => this._renderRow(row))}
        </div>
      </div>
    );
  }

  // A picture of what the card counts, in its own top corner.
  _renderGlyph(glyph, accent) {
    return (
      <i
        className={`fa ${glyph}`}
        aria-hidden="true"
        style={{ position: "absolute", top: 14, right: 16, fontSize: 20, color: accent, opacity: 0.5 }}
      />
    );
  }

  // The state dot, not the text, carries the color: a light categorical hue is
  // unreadable as small text on the card surface.
  _renderRow(row) {
    return (
      <div
        key={row.label}
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "2px 0" }}
      >
        {/* The dot's space is reserved even on a row that has no state to
            show, so the labels line up across cards sitting side by side. */}
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: row.color || "transparent",
            flexShrink: 0,
          }}
        />

        <span style={{ color: INK.muted, flexGrow: 1 }}>
          {row.href ? <a href={row.href} style={{ color: INK.muted }}>{row.label}</a> : row.label}
        </span>

        <span style={{ color: INK.body, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {formatCount(row.value)}
        </span>
      </div>
    );
  }

}

export default HomeStatCard;
